import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  ArrowLeft, Sun, BookOpen, Calculator, Pencil, Flame,
  Loader2, CheckCircle2, Clock, Trophy, Sparkles, ChevronRight, ChevronLeft, PartyPopper, Lightbulb, Volume2, VolumeX,
  Turtle, Rabbit, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import { useReadSpeed, SPEED_PRESETS } from '@/hooks/useReadSpeed';
import { playChime } from '@/utils/sound';
import LikeButton from '@/components/LikeButton';

const SUBJECT_META = {
  reading: { label: 'Reading', icon: BookOpen, color: 'from-rose-400 to-pink-500' },
  math: { label: 'Math', icon: Calculator, color: 'from-amber-400 to-orange-500' },
  writing: { label: 'Writing', icon: Pencil, color: 'from-sky-400 to-indigo-500' },
};

const SummerSlidePage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [activities, setActivities] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(1);

  // Interactive lesson modal state
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [activeActivity, setActiveActivity] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [lessonPhase, setLessonPhase] = useState('steps'); // 'steps' | 'story' | 'worksheet' | 'quiz' | 'done'
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [quizCorrectCount, setQuizCorrectCount] = useState(0);
  const [wsAnswers, setWsAnswers] = useState({});   // {index: value}
  const [wsChecked, setWsChecked] = useState(false);
  const [wsShowHints, setWsShowHints] = useState({});
  const [confetti, setConfetti] = useState(false);
  const [weeksCompletedIds, setWeeksCompletedIds] = useState([]);
  const { speak: speakStory, stop: stopStory, isSpeaking: isReading, speakChunks } = useElevenLabsTTS({ voice: 'shimmer', speed: 0.95 });
  const [highlightedSentence, setHighlightedSentence] = useState(-1);
  const [highlightedWord, setHighlightedWord] = useState(-1);
  const { readSpeed, cycleReadSpeed } = useReadSpeed(childId);
  const wordTimerRef = useRef(null);

  // Speed Memory: `useReadSpeed(childId)` persists the child's chosen speed
  // across sessions via localStorage keyed by profile id.
  // Speed presets live in `hooks/useReadSpeed.js` and are re-exported as SPEED_PRESETS.

  // Split a passage into sentences we can highlight one at a time.
  // Keeps trailing punctuation, filters out empty pieces.
  const splitSentences = (text) => {
    if (!text) return [];
    return String(text)
      .split(/(?<=[.!?])\s+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // Split a sentence into (word + trailing whitespace/punctuation) tokens so we can
  // highlight one word at a time while still rendering the punctuation and spaces.
  const splitWords = (sentence) => {
    if (!sentence) return [];
    // Each match is a run of word chars followed by any trailing punctuation/space.
    const parts = sentence.match(/\S+\s*/g) || [];
    return parts;
  };

  // Advance the word-level highlight for a given sentence by scheduling a
  // series of timers proportional to each word's length. Called by the story
  // reader when a new sentence starts. Cancels any previous timers.
  const startWordHighlighting = (sentence) => {
    if (wordTimerRef.current) {
      wordTimerRef.current.forEach((id) => clearTimeout(id));
      wordTimerRef.current = null;
    }
    const words = splitWords(sentence).map((w) => w.trim()).filter(Boolean);
    if (!words.length) return;
    const preset = SPEED_PRESETS[readSpeed] || SPEED_PRESETS.normal;
    const timers = [];
    let elapsed = 0;
    words.forEach((w, i) => {
      const dur = Math.max(preset.wordBase - 50, preset.wordBase + Math.max(0, w.length - 4) * preset.wordCharBonus);
      const t = setTimeout(() => setHighlightedWord(i), elapsed);
      timers.push(t);
      elapsed += dur;
    });
    timers.push(setTimeout(() => setHighlightedWord(-1), elapsed + 200));
    wordTimerRef.current = timers;
  };

  const clearWordHighlighting = () => {
    if (wordTimerRef.current) {
      wordTimerRef.current.forEach((id) => clearTimeout(id));
      wordTimerRef.current = null;
    }
    setHighlightedWord(-1);
  };

  // Pre-K (age <= 5) auto-read helper
  const isPreK = useMemo(() => {
    if (!child) return false;
    if (child.birth_date) {
      try {
        const dt = new Date(child.birth_date);
        const now = new Date();
        const months = (now.getFullYear() - dt.getFullYear()) * 12 + (now.getMonth() - dt.getMonth());
        return months < 72;
      } catch (e) { /* ignore */ }
    }
    return ['infant','toddler','preschool'].includes(String(child.age_group || '').toLowerCase());
  }, [child]);

  // Auto-narrate the current lesson phase for Pre-K children
  useEffect(() => {
    if (!isPreK || !lessonOpen || !lessonData) return;
    const lesson = lessonData;
    let text = '';
    if (lessonPhase === 'steps') {
      const steps = lesson.steps || [];
      // Include the actual heading + content of each step (the earlier version only
      // read the step number because it looked for `instruction`/`text` which the
      // backend never returns for Summer Slide — the fields are heading + content).
      const spoken = steps.map((s, i) => {
        const head = s.heading || s.title || '';
        const body = s.content || s.instruction || s.text || '';
        return `Step ${i + 1}. ${head}. ${body}`;
      }).join(' ');
      text = `Let's start ${lesson.title || 'today\u2019s lesson'}. ${lesson.intro || ''} ${spoken}`;
    } else if (lessonPhase === 'story') {
      const story = lesson.story || {};
      const passage = story.passage || '';
      text = `${story.title ? story.title + '. ' : ''}${passage}`;
    } else if (lessonPhase === 'worksheet') {
      const ws = lesson.worksheet || {};
      const first = (ws.problems || [])[0];
      text = `${ws.title || 'Worksheet time'}. ${ws.instructions || ''}${first ? ` Let's try. ${first.prompt || ''}` : ''}`;
    } else if (lessonPhase === 'quiz') {
      const q = (lesson.questions || [])[quizIndex];
      if (q) {
        text = `${q.question || ''} ${(q.options || []).map((o, i) => `${['A','B','C','D'][i] || ''}. ${o}`).join('. ')}`;
      }
    } else if (lessonPhase === 'done') {
      text = 'Great job! You finished the lesson.';
    }
    if (text.trim()) {
      const t = setTimeout(() => speakStory(text), 400);
      return () => clearTimeout(t);
    }
  }, [isPreK, lessonOpen, lessonData, lessonPhase, quizIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-narrate quiz feedback
  useEffect(() => {
    if (!isPreK || !quizAnswered || !lessonData) return;
    const q = (lessonData.questions || [])[quizIndex];
    if (!q) return;
    const isRight = selectedAnswer === q.correct;
    const line = isRight
      ? `Yes! That is right. ${q.explanation || ''}`
      : `Not quite. The answer is ${q.correct}. ${q.explanation || ''}`;
    const t = setTimeout(() => speakStory(line), 250);
    return () => clearTimeout(t);
  }, [isPreK, quizAnswered]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [childRes, actsRes, progRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/activities?subject=summer_slide`),
        api.get(`/children/${childId}/progress`).catch(() => ({ data: { activities_completed: [] } })),
      ]);
      setChild(childRes.data);
      setActivities(actsRes.data || []);
      const completed = (progRes.data?.activities_completed || []).reduce((acc, id) => { acc[id] = true; return acc; }, {});
      setProgress(completed);
      setWeeksCompletedIds(childRes.data?.summer_slide?.weeks_completed || []);
    } catch (err) {
      toast.error('Failed to load summer activities');
    } finally {
      setLoading(false);
    }
  };

  const openLesson = async (activity) => {
    setActiveActivity(activity);
    setLessonOpen(true);
    setLessonPhase('steps');
    setQuizIndex(0);
    setSelectedAnswer(null);
    setQuizAnswered(false);
    setQuizCorrectCount(0);
    setLessonData(null);
    setLessonLoading(true);
    setWsAnswers({});
    setWsChecked(false);
    setWsShowHints({});
    try {
      const res = await api.get(`/summer-slide/lesson/${activity.id}`);
      setLessonData(res.data.lesson);
    } catch (err) {
      toast.error("Couldn't load the lesson. Try again in a moment.");
      setLessonOpen(false);
    } finally {
      setLessonLoading(false);
    }
  };

  const submitQuizAnswer = () => {
    if (!selectedAnswer || !lessonData?.questions?.[quizIndex]) return;
    const q = lessonData.questions[quizIndex];
    setQuizAnswered(true);
    if (selectedAnswer === q.correct) {
      setQuizCorrectCount((c) => c + 1);
    }
  };

  const nextQuiz = () => {
    const totalQ = lessonData?.questions?.length || 0;
    if (quizIndex + 1 < totalQ) {
      setQuizIndex((i) => i + 1);
      setSelectedAnswer(null);
      setQuizAnswered(false);
    } else {
      finishLesson();
    }
  };

  const finishLesson = async () => {
    setLessonPhase('done');
    if (activeActivity) {
      await markComplete(activeActivity.id, /*silent*/ true);
      setConfetti(true);
      setTimeout(() => setConfetti(false), 4000);
    }
  };

  const markComplete = async (activityId, silent = false) => {
    try {
      await api.post(`/activities/${activityId}/complete?child_id=${childId}&score=100&time_spent=900`);
      setProgress((prev) => ({ ...prev, [activityId]: true }));
      if (!silent) toast.success('Activity complete! Summer Streak earning');
    } catch (err) {
      setProgress((prev) => ({ ...prev, [activityId]: true }));
    }
  };

  const markWeekComplete = async (weekNum) => {
    try {
      const res = await api.post('/summer-slide/week-complete', { week: weekNum, child_id: childId });
      if (!res.data.already_completed) {
        setWeeksCompletedIds((prev) => [...prev, weekNum]);
        setConfetti(true);
        setTimeout(() => setConfetti(false), 5000);
        toast.success(`Week ${weekNum} conquered! +${res.data.bonus_xp} bonus XP`);
      } else {
        toast.info('Week already marked complete');
      }
    } catch (err) {
      toast.error('Could not mark week complete');
    }
  };

  // Pick the right grade band for this child. Pre-K (preschool + younger) uses the K-1 track
  // as the closest fit — the Pre-K auto-narration handles the reading gap.
  const gradeBand = useMemo(() => {
    const age = child?.age_group;
    if (age === 'infant' || age === 'toddler' || age === 'preschool') return 'k1';
    if (age === 'early_elementary') return 'k1'; // K-1 catches younger; g23 also available
    if (age === 'elementary') return 'g45';
    if (age === 'middle') return 'g68';
    return null;
  }, [child]);

  const ageGroupActivities = useMemo(() => {
    return activities.filter((a) => {
      const ag = child?.age_group;
      if (!ag) return true;
      // Pre-K children (preschool + younger) use the K-1 track since we don't have
      // preK-tagged Summer Slide content yet. Auto-narration reads it aloud for them.
      const allowedGroups = ['infant', 'toddler', 'preschool'].includes(ag)
        ? ['early_elementary']
        : [ag];
      const activityGroups = a.age_groups || [];
      return allowedGroups.some((g) => activityGroups.includes(g));
    });
  }, [activities, child]);

  // Group by week
  const weekData = useMemo(() => {
    const byWeek = {};
    ageGroupActivities.forEach((a) => {
      const w = a.content?.week;
      if (!w) return;
      if (!byWeek[w]) byWeek[w] = { week: w, theme: a.content.theme, days: [] };
      byWeek[w].days.push(a);
    });
    return Object.values(byWeek).sort((x, y) => x.week - y.week);
  }, [ageGroupActivities]);

  const totalCompleted = ageGroupActivities.filter((a) => progress[a.id]).length;
  const overallProgress = ageGroupActivities.length ? Math.round((totalCompleted / ageGroupActivities.length) * 100) : 0;
  const weeksCompleted = weekData.filter((w) => w.days.every((d) => progress[d.id])).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-white">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-amber-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/learn/${childId}`)} data-testid="summer-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Sun className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl text-slate-900">Summer Slide Prevention</h1>
                <p className="text-xs text-slate-600">15 minutes a day keeps the slide away, {child?.name}!</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-slate-500">Summer Streak</p>
              <p className="text-2xl font-extrabold text-orange-600 flex items-center gap-1">
                <Flame className="w-5 h-5" />{weeksCompleted}w
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Total progress</p>
              <p className="text-2xl font-extrabold text-amber-700">{overallProgress}%</p>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-3">
          <Progress value={overallProgress} className="h-2 bg-amber-100" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {!gradeBand && (
          <Card className="mb-6 bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-sm text-amber-800">
              Add a birthday to your child&apos;s profile to unlock the right summer plan for their grade level.
            </CardContent>
          </Card>
        )}

        <Tabs value={String(currentWeek)} onValueChange={(v) => setCurrentWeek(parseInt(v))}>
          <div className="overflow-x-auto pb-2 mb-4">
            <TabsList className="bg-white/80 border border-amber-200 inline-flex">
              {weekData.map((w) => {
                const wCompleted = w.days.every((d) => progress[d.id]);
                return (
                  <TabsTrigger
                    key={w.week}
                    value={String(w.week)}
                    className="data-[state=active]:bg-amber-500 data-[state=active]:text-white whitespace-nowrap"
                    data-testid={`summer-week-${w.week}`}
                  >
                    Week {w.week} {wCompleted && '✓'}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {weekData.map((w) => {
            const allDaysDone = w.days.length > 0 && w.days.every((d) => progress[d.id]);
            const weekMarked = weeksCompletedIds.includes(w.week);
            return (
            <TabsContent key={w.week} value={String(w.week)}>
              <div className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-white">
                <p className="text-sm uppercase tracking-wider text-amber-50">Week {w.week} Theme</p>
                <h2 className="text-3xl font-extrabold mt-1">{w.theme}</h2>
                <p className="text-amber-50 mt-1">5 days of fun, focused practice — 15 minutes each.</p>
              </div>

              <div className="space-y-6">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day, dIdx) => {
                  const dayNum = dIdx + 1;
                  const dayActs = w.days.filter((a) => a.content?.day === dayNum);
                  return (
                    <div key={day}>
                      <h3 className="font-bold text-slate-800 mb-3 text-lg">{day}</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        {Object.keys(SUBJECT_META).map((subj) => {
                          const meta = SUBJECT_META[subj];
                          const Icon = meta.icon;
                          const act = dayActs.find((a) => a.content?.subject === subj);
                          if (!act) return (
                            <Card key={subj} className="bg-slate-50/50 border-dashed">
                              <CardContent className="p-4 text-center text-xs text-slate-400 py-8">
                                <Icon className="w-5 h-5 mx-auto mb-1 opacity-30" />
                                Coming soon
                              </CardContent>
                            </Card>
                          );
                          const isDone = progress[act.id];
                          return (
                            <Card
                              key={subj}
                              className={`overflow-hidden transition-all ${isDone ? 'ring-1 ring-emerald-400 bg-emerald-50/50' : 'hover:shadow-md'}`}
                              data-testid={`summer-activity-${act.id}`}
                            >
                              <div className={`bg-gradient-to-r ${meta.color} px-4 py-2 flex items-center justify-between`}>
                                <div className="flex items-center gap-2 text-white">
                                  <Icon className="w-4 h-4" />
                                  <span className="font-semibold text-sm">{meta.label}</span>
                                </div>
                                {isDone && <CheckCircle2 className="w-5 h-5 text-white" />}
                              </div>
                              <CardContent className="p-4">
                                <p className="text-sm text-slate-700 mb-3 leading-relaxed">{act.instructions}</p>
                                <div className="flex items-center gap-2 mb-3 flex-wrap">
                                  <Badge variant="outline" className="text-xs">
                                    <Clock className="w-3 h-3 mr-1" /> 15 min
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    +{act.xp_reward} XP
                                  </Badge>
                                </div>
                                {!isDone ? (
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                                      onClick={() => openLesson(act)}
                                      data-testid={`summer-start-lesson-${act.id}`}
                                    >
                                      <Sparkles className="w-4 h-4 mr-1" />
                                      Start Lesson
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => markComplete(act.id)}
                                      data-testid={`summer-complete-${act.id}`}
                                      title="I already did this activity"
                                    >
                                      <Trophy className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Badge className="w-full justify-center bg-emerald-100 text-emerald-700 py-1">
                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                                  </Badge>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mark Week Complete */}
              <div className="mt-8">
                {weekMarked ? (
                  <Card className="border-2 border-emerald-300 bg-emerald-50">
                    <CardContent className="p-4 flex items-center gap-3 justify-center">
                      <PartyPopper className="w-6 h-6 text-emerald-600" />
                      <p className="font-semibold text-emerald-800">Week {w.week} conquered! +100 bonus XP</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-6 text-base font-bold"
                    disabled={!allDaysDone}
                    onClick={() => markWeekComplete(w.week)}
                    data-testid={`summer-mark-week-${w.week}`}
                  >
                    <Trophy className="w-5 h-5 mr-2" />
                    {allDaysDone ? `Mark Week ${w.week} Complete (+100 XP)` : `Finish all 5 days to complete Week ${w.week}`}
                  </Button>
                )}
              </div>
            </TabsContent>
            );
          })}
        </Tabs>
      </main>

      {/* Interactive Lesson Dialog */}
      {confetti && <Confetti recycle={false} numberOfPieces={220} />}
      <Dialog open={lessonOpen} onOpenChange={(o) => { if (!o) { setLessonOpen(false); stopStory(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {lessonLoading && (
            <>
              <DialogHeader>
                <DialogTitle className="sr-only">Loading Lesson</DialogTitle>
              </DialogHeader>
              <div className="py-16 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                <p className="text-sm text-slate-500">Generating a fun lesson for you…</p>
              </div>
            </>
          )}

          {!lessonLoading && lessonData && lessonPhase === 'steps' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{lessonData.title}</DialogTitle>
                <DialogDescription>{lessonData.intro}</DialogDescription>
              </DialogHeader>
              <div className="flex justify-end mt-2">
                {isReading ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => stopStory()}
                    data-testid="summer-steps-tts-stop"
                  >
                    <VolumeX className="w-4 h-4 mr-1" /> Stop reading
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => {
                      const steps = lessonData.steps || [];
                      const spoken = steps.map((s, i) => `Step ${i + 1}. ${s.heading || s.title || ''}. ${s.content || s.instruction || s.text || ''}`).join(' ');
                      speakStory(`${lessonData.title || ''}. ${lessonData.intro || ''} ${spoken}`);
                    }}
                    data-testid="summer-steps-tts-play"
                  >
                    <Volume2 className="w-4 h-4 mr-1" /> Read to me
                  </Button>
                )}
              </div>
              <div className="space-y-4 mt-4">
                {(lessonData.steps || []).map((s) => (
                  <Card key={s.step} className="border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                          {s.step}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-bold text-slate-800">{s.heading}</h4>
                            <button
                              type="button"
                              onClick={() => speakStory(`${s.heading || ''}. ${s.content || ''}`)}
                              className="text-amber-500 hover:text-amber-700 shrink-0"
                              aria-label={`Read step ${s.step} aloud`}
                              data-testid={`summer-step-tts-${s.step}`}
                            >
                              <Volume2 className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">{s.content}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600 mt-6"
                onClick={() => {
                  const lesson = lessonData?.lesson || lessonData;
                  if (lesson?.story?.passage) setLessonPhase('story');
                  else if (lesson?.worksheet?.problems?.length) setLessonPhase('worksheet');
                  else setLessonPhase('quiz');
                }}
                data-testid="summer-lesson-continue"
              >
                {(lessonData?.lesson || lessonData)?.story?.passage
                  ? 'Read the story'
                  : ((lessonData?.lesson || lessonData)?.worksheet?.problems?.length ? 'Open worksheet' : 'Ready! Try the quick check')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}

          {/* STORY PHASE — reading */}
          {!lessonLoading && lessonData && lessonPhase === 'story' && (() => {
            const lesson = lessonData.lesson || lessonData;
            const story = lesson.story || {};
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-rose-500" /> {story.title || 'Story time'}
                  </DialogTitle>
                  <DialogDescription>Read carefully — comprehension questions after.</DialogDescription>
                </DialogHeader>
                <div className="mt-4 space-y-4" data-testid="summer-lesson-story">
                  <div className="flex justify-end items-center gap-2">
                    <button
                      type="button"
                      onClick={cycleReadSpeed}
                      className={`flex items-center gap-1.5 px-3 h-9 rounded-full border-2 text-sm font-semibold transition-colors ${
                        readSpeed === 'slow'
                          ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                          : readSpeed === 'fast'
                            ? 'border-orange-400 bg-orange-50 text-orange-700'
                            : 'border-slate-300 bg-white text-slate-600'
                      }`}
                      aria-label={`Reading speed: ${SPEED_PRESETS[readSpeed].label}. Tap to change.`}
                      title={`Reading speed: ${SPEED_PRESETS[readSpeed].label}. Tap to cycle slow / normal / fast.`}
                      data-testid={`read-speed-toggle-${readSpeed}`}
                    >
                      {readSpeed === 'slow' ? (
                        <Turtle className="w-4 h-4" />
                      ) : readSpeed === 'normal' ? (
                        <Rabbit className="w-4 h-4" />
                      ) : (
                        <><Rabbit className="w-4 h-4" /><Zap className="w-3 h-3 -ml-1 text-orange-500" /></>
                      )}
                      <span>{SPEED_PRESETS[readSpeed].label}</span>
                    </button>
                    {isReading ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-rose-300 text-rose-600 hover:bg-rose-50"
                        onClick={() => { stopStory(); setHighlightedSentence(-1); clearWordHighlighting(); }}
                        data-testid="summer-story-tts-stop"
                      >
                        <VolumeX className="w-4 h-4 mr-1" />
                        Stop reading
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-rose-500 hover:bg-rose-600 text-white"
                        onClick={() => {
                          const passage = story.passage || '';
                          const sentences = splitSentences(passage);
                          const chunks = story.title
                            ? [`${story.title}.`, ...sentences]
                            : sentences;
                          const offset = story.title ? -1 : 0;
                          const preset = SPEED_PRESETS[readSpeed] || SPEED_PRESETS.normal;
                          speakChunks(chunks, (i) => {
                            if (i < 0) {
                              setHighlightedSentence(-1);
                              clearWordHighlighting();
                              return;
                            }
                            const sentenceIdx = i + offset;
                            setHighlightedSentence(sentenceIdx);
                            if (sentenceIdx >= 0) {
                              startWordHighlighting(sentences[sentenceIdx] || '');
                            } else {
                              clearWordHighlighting();
                            }
                          }, { speed: preset.voice });
                        }}
                        data-testid="summer-story-tts-play"
                      >
                        <Volume2 className="w-4 h-4 mr-1" />
                        Read to me
                      </Button>
                    )}
                  </div>
                  <Card className="border-rose-200 bg-rose-50/50">
                    <CardContent className="p-5">
                      {(() => {
                        const sentences = splitSentences(story.passage || '');
                        if (!sentences.length) {
                          return <p className="text-slate-800 leading-relaxed whitespace-pre-line">{story.passage}</p>;
                        }
                        return (
                          <p className="text-slate-800 leading-relaxed" data-testid="story-passage">
                            {sentences.map((s, i) => {
                              const isActive = i === highlightedSentence;
                              // For non-active sentences render as one span (cheap).
                              // For the active sentence, split into word tokens so we
                              // can highlight one word at a time as the narrator reads.
                              if (!isActive) {
                                return (
                                  <span key={i} data-testid={`story-sentence-${i}`}>
                                    {s}{i < sentences.length - 1 ? ' ' : ''}
                                  </span>
                                );
                              }
                              const tokens = splitWords(s);
                              return (
                                <span
                                  key={i}
                                  data-testid={`story-sentence-${i}`}
                                  className="bg-amber-100 rounded-md shadow-inner ring-2 ring-amber-200 px-1 py-0.5"
                                >
                                  {tokens.map((tok, wi) => (
                                    <span
                                      key={wi}
                                      className={`transition-all duration-150 ${
                                        wi === highlightedWord
                                          ? 'bg-amber-500 text-white rounded px-0.5 py-0.5 font-bold'
                                          : ''
                                      }`}
                                      data-testid={`story-word-${i}-${wi}`}
                                    >
                                      {tok}
                                    </span>
                                  ))}
                                  {i < sentences.length - 1 ? ' ' : ''}
                                </span>
                              );
                            })}
                          </p>
                        );
                      })()}
                    </CardContent>
                  </Card>
                  {Array.isArray(story.vocab) && story.vocab.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Words to know</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {story.vocab.map((v, i) => (
                          <div key={i} className="text-sm bg-white border border-slate-200 rounded-lg px-3 py-2">
                            <span className="font-bold text-rose-700">{v.word}</span>
                            <span className="text-slate-600"> — {v.meaning}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-6">
                  <Button variant="outline" onClick={() => setLessonPhase('steps')} data-testid="summer-story-back">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back to lesson
                  </Button>
                  {activeActivity && (
                    <div className="self-center">
                      <LikeButton
                        childId={childId}
                        contentType="summer_slide"
                        contentId={activeActivity.id}
                        size="md"
                        metadata={{
                          title: story.title || activeActivity.title,
                          subject: activeActivity.content?.subject,
                          week: activeActivity.content?.week,
                          preview: (story.passage || '').slice(0, 120),
                        }}
                        testIdPrefix="summer-story-like"
                      />
                    </div>
                  )}
                  <Button
                    className="flex-1 bg-rose-500 hover:bg-rose-600"
                    onClick={() => {
                      if (lesson.worksheet?.problems?.length) setLessonPhase('worksheet');
                      else setLessonPhase('quiz');
                    }}
                    data-testid="summer-story-next"
                  >
                    {lesson.worksheet?.problems?.length ? 'Now try the worksheet' : 'Ready for questions'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </>
            );
          })()}

          {/* WORKSHEET PHASE — math */}
          {!lessonLoading && lessonData && lessonPhase === 'worksheet' && (() => {
            const lesson = lessonData.lesson || lessonData;
            const ws = lesson.worksheet || {};
            const problems = ws.problems || [];
            const normalize = (v) => String(v ?? '').trim().toLowerCase();
            const correctCount = problems.filter((p, i) => normalize(wsAnswers[i]) === normalize(p.answer)).length;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl flex items-center gap-2">
                    <Calculator className="w-6 h-6 text-amber-500" /> {ws.title || 'Math worksheet'}
                  </DialogTitle>
                  <DialogDescription>Solve each problem. Peek at a hint if you get stuck.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-4" data-testid="summer-lesson-worksheet">
                  {problems.map((p, i) => {
                    const val = wsAnswers[i] || '';
                    const correct = normalize(val) === normalize(p.answer);
                    return (
                      <Card key={i} className={`border-2 ${wsChecked ? (correct ? 'border-emerald-300 bg-emerald-50/40' : 'border-red-300 bg-red-50/40') : 'border-slate-200'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="text-sm text-slate-700"><span className="font-bold text-amber-600">Q{i + 1}.</span> {p.prompt}</p>
                            <button
                              type="button"
                              onClick={() => speakStory(`Question ${i + 1}. ${p.prompt}`)}
                              className="text-amber-500 hover:text-amber-700 shrink-0 mt-0.5"
                              aria-label={`Read question ${i + 1} aloud`}
                              data-testid={`ws-tts-${i}`}
                            >
                              <Volume2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center">
                            <input
                              type="text"
                              value={val}
                              onChange={(e) => setWsAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                              disabled={wsChecked}
                              placeholder="Your answer"
                              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-slate-100"
                              data-testid={`ws-answer-${i}`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setWsShowHints((prev) => {
                                const isOpening = !prev[i];
                                if (isOpening) playChime();
                                return { ...prev, [i]: !prev[i] };
                              })}
                              data-testid={`ws-hint-${i}`}
                            >
                              <Lightbulb className="w-3 h-3 mr-1 text-amber-500" /> {wsShowHints[i] ? 'Hide hint' : 'Hint'}
                            </Button>
                            {wsChecked && (
                              <span className={`text-xs font-semibold ${correct ? 'text-emerald-700' : 'text-red-700'}`}>
                                {correct ? '✓ correct' : `Answer: ${p.answer}`}
                              </span>
                            )}
                          </div>
                          {wsShowHints[i] && p.hint && (
                            <p className="mt-2 text-xs text-amber-800 bg-amber-100 rounded-md px-2 py-1"><Lightbulb className="w-3 h-3 inline mr-1" />{p.hint}</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                {wsChecked && (
                  <div className="mt-3 p-3 rounded-md bg-emerald-50 text-emerald-800 text-sm text-center font-semibold" data-testid="ws-score">
                    You got {correctCount} of {problems.length} right! 🎯
                  </div>
                )}
                <div className="flex gap-2 mt-6">
                  <Button variant="outline" onClick={() => {
                    if (lesson.story?.passage) setLessonPhase('story');
                    else setLessonPhase('steps');
                  }} data-testid="summer-worksheet-back">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  {!wsChecked ? (
                    <Button
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      onClick={() => setWsChecked(true)}
                      data-testid="summer-worksheet-check"
                      disabled={Object.keys(wsAnswers).length === 0}
                    >
                      Check my worksheet
                    </Button>
                  ) : (
                    <Button
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      onClick={() => setLessonPhase('quiz')}
                      data-testid="summer-worksheet-next"
                    >
                      Ready for the quick check
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </>
            );
          })()}

          {!lessonLoading && lessonData && lessonPhase === 'quiz' && lessonData.questions?.[quizIndex] && (
            <>
              <DialogHeader>
                <DialogTitle>Quick Check — Question {quizIndex + 1} of {lessonData.questions.length}</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <div className="flex items-start justify-between gap-2 mb-4">
                  <p className="font-medium text-slate-800">{lessonData.questions[quizIndex].question}</p>
                  <button
                    type="button"
                    onClick={() => {
                      const q = lessonData.questions[quizIndex];
                      const spoken = `${q.question || ''}. ${(q.options || []).map((o, i) => `${['A','B','C','D'][i] || ''}. ${o}`).join('. ')}`;
                      speakStory(spoken);
                    }}
                    className="text-amber-500 hover:text-amber-700 shrink-0 mt-0.5"
                    aria-label="Read this question aloud"
                    data-testid={`quiz-tts-${quizIndex}`}
                  >
                    <Volume2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2">
                  {(lessonData.questions[quizIndex].options || []).map((opt, i) => {
                    const isSelected = selectedAnswer === opt;
                    const isCorrect = opt === lessonData.questions[quizIndex].correct;
                    let style = 'border-slate-200 hover:border-amber-400 hover:bg-amber-50';
                    if (quizAnswered) {
                      if (isCorrect) style = 'border-emerald-500 bg-emerald-50 text-emerald-800';
                      else if (isSelected && !isCorrect) style = 'border-red-500 bg-red-50 text-red-700';
                      else style = 'border-slate-200 bg-slate-50 text-slate-500';
                    } else if (isSelected) style = 'border-amber-500 bg-amber-50';
                    return (
                      <button
                        key={i}
                        onClick={() => !quizAnswered && setSelectedAnswer(opt)}
                        disabled={quizAnswered}
                        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${style}`}
                        data-testid={`summer-quiz-option-${i}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {quizAnswered && (
                  <div className="mt-4 p-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800">
                    {lessonData.questions[quizIndex].explanation}
                  </div>
                )}
                <div className="mt-6">
                  {!quizAnswered ? (
                    <Button
                      className="w-full bg-amber-500 hover:bg-amber-600"
                      disabled={!selectedAnswer}
                      onClick={submitQuizAnswer}
                      data-testid="summer-quiz-check"
                    >
                      Check Answer
                    </Button>
                  ) : (
                    <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={nextQuiz} data-testid="summer-quiz-next">
                      {quizIndex + 1 < lessonData.questions.length ? 'Next Question' : 'Finish Lesson'}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {!lessonLoading && lessonData && lessonPhase === 'done' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-2">
                  <PartyPopper className="w-6 h-6 text-amber-500" /> Awesome work!
                </DialogTitle>
              </DialogHeader>
              <div className="text-center py-6">
                <div className="text-6xl mb-3">🎉</div>
                <p className="text-lg text-slate-700 mb-1">{lessonData.wrap_up}</p>
                <p className="text-sm text-slate-500 mb-6">
                  You answered {quizCorrectCount} of {lessonData.questions.length} correctly.
                </p>
                <Button
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500"
                  onClick={() => setLessonOpen(false)}
                  data-testid="summer-lesson-close"
                >
                  Back to my summer plan
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SummerSlidePage;
